import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createTaxCase } from '../../services/cases';
import { getUserById } from '../../services/users';
import { useAuth } from '../../hooks/useAuth';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { ApiClientError } from '../../services/api';

export function CreateCasePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const clientId = searchParams.get('clientId');

  const [taxYear, setTaxYear] = useState<string>(new Date().getFullYear().toString());
  const [notes, setNotes] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const { data: client, isLoading: clientLoading, error: clientError } = useQuery({
    queryKey: ['client', clientId],
    queryFn: () => getUserById(clientId!),
    enabled: !!clientId,
  });

  const createMutation = useMutation({
    mutationFn: createTaxCase,
    onSuccess: (data) => {
      navigate(`/admin/cases/${data.id}`, {
        state: { message: 'Tax case created successfully' }
      });
    },
    onError: (err) => {
      const message = err instanceof ApiClientError
        ? err.message
        : 'Failed to create tax case. Please try again.';
      setError(message);
    },
  });

  useEffect(() => {
    if (!clientId) {
      setError('Client ID is required. Please select a client first.');
    } else if (clientError) {
      setError('Failed to load client information. Please try again.');
    }
  }, [clientId, clientError]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!clientId) {
      setError('Client ID is required');
      return;
    }

    if (!user?.id) {
      setError('You must be logged in to create a case');
      return;
    }

    const yearNumber = parseInt(taxYear, 10);
    if (isNaN(yearNumber) || yearNumber < 2000 || yearNumber > 2100) {
      setError('Please enter a valid tax year between 2000 and 2100');
      return;
    }

    createMutation.mutate({
      clientId,
      adminId: user.id,
      taxYear: yearNumber,
      notes: notes.trim() || null,
    });
  };

  if (!clientId) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <span className="material-symbols-outlined text-6xl text-error">error</span>
          <h2 className="mt-4 text-2xl font-bold text-error">Missing Client ID</h2>
          <p className="mt-2 text-on-surface/70">
            Please select a client from the directory to create a case.
          </p>
          <Button onClick={() => navigate('/admin/clients')} className="mt-6">
            Go to Client Directory
          </Button>
        </div>
      </div>
    );
  }

  if (clientLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="inline-flex items-center gap-3 text-on-surface/60">
          <span className="material-symbols-outlined animate-spin">refresh</span>
          <span className="font-semibold">Loading client information...</span>
        </div>
      </div>
    );
  }

  if (clientError || !client) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <span className="material-symbols-outlined text-6xl text-error">error</span>
          <h2 className="mt-4 text-2xl font-bold text-error">Failed to Load Client</h2>
          <p className="mt-2 text-on-surface/70">
            {clientError instanceof Error ? clientError.message : 'Unable to load client information'}
          </p>
          <Button onClick={() => navigate('/admin/clients')} className="mt-6">
            Back to Client Directory
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <header className="mb-8">
        <div className="mb-2 flex items-center gap-2">
          <Link
            to={`/admin/clients/${clientId}`}
            className="flex items-center gap-1 text-sm font-semibold text-secondary transition-colors hover:text-primary"
          >
            <span className="material-symbols-outlined text-lg">arrow_back</span>
            Back to Client
          </Link>
        </div>
        <h1 className="text-3xl font-bold text-primary sm:text-4xl lg:text-5xl">
          Create New Tax Case
        </h1>
        <p className="mt-2 text-base leading-relaxed text-on-surface/70 sm:text-lg">
          Create a new tax case for {client.firstName} {client.lastName}
        </p>
      </header>

      <div className="mx-auto max-w-2xl">
        <Card className="bg-surface-container-low">
          <CardHeader>
            <CardTitle>Case Details</CardTitle>
            <CardDescription>
              Enter the tax year and any relevant notes for this case
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="rounded-lg bg-primary-container/20 p-4">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-container">
                    <span className="material-symbols-outlined text-2xl text-on-primary-container">person</span>
                  </div>
                  <div>
                    <p className="label-sm text-on-surface/60">Client</p>
                    <p className="font-semibold text-on-surface">
                      {client.firstName} {client.lastName}
                    </p>
                    <p className="text-sm text-on-surface/70">{client.email}</p>
                  </div>
                </div>
              </div>

              <Input
                label="Tax Year"
                type="number"
                value={taxYear}
                onChange={(e) => setTaxYear(e.target.value)}
                placeholder={new Date().getFullYear().toString()}
                min="2000"
                max="2100"
                required
                error={error && error.includes('tax year') ? error : undefined}
              />

              <div className="flex w-full flex-col gap-2 text-left">
                <label htmlFor="notes" className="label-sm text-on-surface/70">
                  Notes (Optional)
                </label>
                <textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any relevant notes or context for this tax case..."
                  rows={5}
                  className="w-full rounded-lg border-2 border-outline-variant/30 bg-surface-variant/20 px-4 py-3 text-on-surface outline-none transition-all focus:border-primary focus:bg-surface-variant/30"
                />
              </div>

              {error && !error.includes('tax year') && (
                <div className="rounded-lg bg-error-container p-4">
                  <div className="flex items-start gap-3">
                    <span className="material-symbols-outlined text-xl text-error">error</span>
                    <div className="flex-1">
                      <p className="font-semibold text-error">Error</p>
                      <p className="text-sm text-on-error-container">{error}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setError(null)}
                      className="text-on-error-container hover:text-error"
                    >
                      <span className="material-symbols-outlined text-sm">close</span>
                    </button>
                  </div>
                </div>
              )}

              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => navigate(`/admin/clients/${clientId}`)}
                  disabled={createMutation.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  loading={createMutation.isPending}
                  disabled={createMutation.isPending}
                >
                  <span className="material-symbols-outlined mr-2 text-sm">add</span>
                  {createMutation.isPending ? 'Creating Case...' : 'Create Tax Case'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card className="mt-6 bg-surface-container-low">
          <CardHeader>
            <CardTitle>What happens next?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white">
                  <span className="text-sm font-bold">1</span>
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-on-surface">Case Created</p>
                  <p className="text-sm text-on-surface/70">
                    The tax case will start in the "Onboarding" stage
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white">
                  <span className="text-sm font-bold">2</span>
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-on-surface">Upload Documents</p>
                  <p className="text-sm text-on-surface/70">
                    Upload tax documents and move the case through the pipeline
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white">
                  <span className="text-sm font-bold">3</span>
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-on-surface">Client Signature</p>
                  <p className="text-sm text-on-surface/70">
                    Send documents for electronic signature when ready
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-white">
                  <span className="text-sm font-bold">4</span>
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-on-surface">Payment & Completion</p>
                  <p className="text-sm text-on-surface/70">
                    Manage payments and complete the tax filing process
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
